import React, { useState, useCallback, useRef, useEffect, useMemo, memo } from 'react';
import { Modal, Spinner } from 'react-bootstrap';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';

pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

const ZOOM_STEPS = [50, 75, 100, 125, 150, 175, 200, 250, 300];

// Escape HTML entities so PDF text is safe to inject into innerHTML
function escapeHtml(str) {
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/* ─────────────────────────────────────────────────────────────────────
   Chunk-highlight matching engine.

   The chat backend attaches the top reranked retrieval chunks to each
   source as `highlights: [{ text, page }]`. Chunk text comes from OCR
   markdown while the PDF text layer is pdf.js text items (arbitrary
   fragments that can split mid-word), so matching works on a normalized
   alphanumeric-only projection of both sides:

   1. Each page's text items are concatenated into one normalized string
      (lowercase, [a-z0-9] only) with a char→(item, offset) map.
   2. The chunk is matched as a whole; failing that, overlapping 5-word
      shingle anchors are located individually and merged into spans.
   3. Matched normalized ranges are mapped back to per-item character
      ranges, which customTextRenderer wraps in <mark>.
───────────────────────────────────────────────────────────────────── */

const normalizePlain = (text) => (text || '').toLowerCase().replace(/[^a-z0-9]+/g, '');

// Concatenate item strings into one normalized string, mapping every kept
// char back to its text item and offset within that item.
function normalizeItems(itemStrs) {
  let norm = '';
  const map = [];
  itemStrs.forEach((s, item) => {
    const lower = (s || '').toLowerCase();
    for (let off = 0; off < lower.length; off++) {
      const c = lower[off];
      if ((c >= 'a' && c <= 'z') || (c >= '0' && c <= '9')) {
        norm += c;
        map.push({ item, off });
      }
    }
  });
  return { norm, map };
}

// Overlapping 5-word shingles from the chunk, normalized. These are the
// anchors located individually when the full chunk doesn't match verbatim.
function buildAnchors(text) {
  const words = (text || '').toLowerCase().split(/\s+/).filter(Boolean);
  const anchors = [];
  const SIZE = 5, STRIDE = 3;
  for (let i = 0; i + SIZE <= words.length; i += STRIDE) {
    const a = normalizePlain(words.slice(i, i + SIZE).join(''));
    if (a.length >= 12) anchors.push(a);
  }
  if (anchors.length === 0) {
    const whole = normalizePlain(text);
    if (whole.length >= 8) anchors.push(whole);
  }
  return anchors;
}

// Find the chunk within one page's normalized text.
// Returns { ranges: [[start, end), ...], score } in normalized coordinates.
function findRangesInPage(norm, needleNorm, anchors) {
  if (needleNorm.length >= 12) {
    const idx = norm.indexOf(needleNorm);
    if (idx !== -1) {
      return { ranges: [[idx, idx + needleNorm.length]], score: anchors.length + 1 };
    }
  }
  const hits = [];
  for (const a of anchors) {
    const idx = norm.indexOf(a);
    if (idx !== -1) hits.push([idx, idx + a.length]);
  }
  if (hits.length === 0) return { ranges: [], score: 0 };
  hits.sort((x, y) => x[0] - y[0]);
  const merged = [hits[0].slice()];
  for (let i = 1; i < hits.length; i++) {
    const last = merged[merged.length - 1];
    if (hits[i][0] <= last[1] + 30) last[1] = Math.max(last[1], hits[i][1]);
    else merged.push(hits[i].slice());
  }
  return { ranges: merged, score: hits.length };
}

// Locate every highlight chunk in the document.
// Returns { pages: { pageNum: { itemIndex: [[start, end), ...] } }, firstPage }.
async function computeHighlightMatches(pdf, highlights) {
  const numPages = pdf.numPages;
  const pageCache = new Map();

  const getPageNorm = async (p) => {
    if (!pageCache.has(p)) {
      const page = await pdf.getPage(p);
      const textContent = await page.getTextContent();
      pageCache.set(p, normalizeItems(textContent.items.map(it => it.str)));
    }
    return pageCache.get(p);
  };

  const pages = {};
  let firstPage = null;

  for (const h of highlights) {
    const needleNorm = normalizePlain(h.text);
    if (needleNorm.length < 8) continue;
    const anchors = buildAnchors(h.text);
    const fullScore = anchors.length + 1;
    const minScore = Math.max(1, Math.ceil(anchors.length * 0.25));

    // Try the hinted page (and neighbors — OCR page markers can be off by
    // one) before scanning the whole document.
    const order = [];
    if (h.page >= 1) order.push(h.page, h.page + 1, h.page - 1);
    for (let p = 1; p <= numPages; p++) order.push(p);

    const seen = new Set();
    let best = null;
    for (const p of order) {
      if (p < 1 || p > numPages || seen.has(p)) continue;
      seen.add(p);
      const { norm, map } = await getPageNorm(p);
      const { ranges, score } = findRangesInPage(norm, needleNorm, anchors);
      if (score > (best ? best.score : 0)) best = { page: p, ranges, map, score };
      if (score >= fullScore) break; // verbatim match — stop scanning
    }
    if (!best || best.score < minScore) continue;

    // Map normalized ranges back to per-item character ranges
    const pageEntry = pages[best.page] || (pages[best.page] = {});
    for (const [s, e] of best.ranges) {
      let i = s;
      while (i < e && i < best.map.length) {
        const item = best.map[i].item;
        let j = i;
        while (j < e && j < best.map.length && best.map[j].item === item) j++;
        (pageEntry[item] || (pageEntry[item] = [])).push([best.map[i].off, best.map[j - 1].off + 1]);
        i = j;
      }
    }
    if (firstPage === null) firstPage = best.page;
  }

  // Merge overlapping per-item ranges so <mark> spans never nest
  for (const p of Object.keys(pages)) {
    for (const item of Object.keys(pages[p])) {
      const rs = pages[p][item].sort((a, b) => a[0] - b[0]);
      const merged = [rs[0].slice()];
      for (let i = 1; i < rs.length; i++) {
        const last = merged[merged.length - 1];
        if (rs[i][0] <= last[1]) last[1] = Math.max(last[1], rs[i][1]);
        else merged.push(rs[i].slice());
      }
      pages[p][item] = merged;
    }
  }

  return { pages, firstPage };
}

/* ─────────────────────────────────────────────────────────────────────
   PagePlaceholder: correctly-sized white box that triggers lazy render
   via IntersectionObserver when it scrolls into view.
───────────────────────────────────────────────────────────────────── */
const PagePlaceholder = memo(({ pageNum, scale, rotation, scrollRoot, onVisible }) => {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) onVisible(pageNum); },
      { root: scrollRoot?.current ?? null, threshold: 0.01, rootMargin: '500px 0px' }
    );
    observer.observe(el);
    return () => observer.unobserve(el);
  }, [pageNum, onVisible, scrollRoot]);

  const landscape = rotation === 90 || rotation === 270;
  const w = Math.round((landscape ? 842 : 595) * scale);
  const h = Math.round((landscape ? 595 : 842) * scale);
  return (
    <div
      ref={ref}
      style={{
        width: w, height: h, background: 'white', borderRadius: 2,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        boxShadow: '0 4px 20px rgba(0,0,0,0.45)', color: '#aaa',
        fontSize: 13, userSelect: 'none', flexShrink: 0,
      }}
    >
      {pageNum}
    </div>
  );
});

/* ─────────────────────────────────────────────────────────────────────
   LazyThumbnail: page preview with its own IntersectionObserver
───────────────────────────────────────────────────────────────────── */
const LazyThumbnail = memo(({ pageNum, currentPage, onJump, sidebarRef }) => {
  const [rendered, setRendered] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el || rendered) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setRendered(true); },
      { root: sidebarRef?.current ?? null, threshold: 0.01, rootMargin: '200px 0px' }
    );
    observer.observe(el);
    return () => observer.unobserve(el);
  }, [rendered, sidebarRef]);

  return (
    <div
      ref={ref}
      className={`pdf-thumbnail-item${pageNum === currentPage ? ' active' : ''}`}
      onClick={() => onJump(pageNum)}
      title={`Go to page ${pageNum}`}
    >
      {rendered
        ? <Page pageNumber={pageNum} width={110} renderTextLayer={false} renderAnnotationLayer={false} />
        : <div style={{ width: 110, height: 155, background: '#f0f0f0' }} />
      }
      <span className="pdf-thumbnail-num">{pageNum}</span>
    </div>
  );
});

/* ─────────────────────────────────────────────────────────────────────
   Main PDFViewerEnhanced component
───────────────────────────────────────────────────────────────────── */
const PDFViewerEnhanced = ({ show, onHide, pdfUrl, filename, searchText = null, pageNumber: initialPage = null, highlights = [] }) => {
  const [numPages, setNumPages] = useState(null);
  const [pdfDoc, setPdfDoc] = useState(null);
  const [highlightMatches, setHighlightMatches] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageInput, setPageInput] = useState('1');
  const [scale, setScale] = useState(1.2);
  const [rotation, setRotation] = useState(0);
  const [showSearchBar, setShowSearchBar] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showThumbnails, setShowThumbnails] = useState(false);
  const [renderedPages, setRenderedPages] = useState(new Set());
  const [isPrinting, setIsPrinting] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);

  const scrollContainerRef = useRef(null);
  const sidebarRef = useRef(null);
  const pageRefs = useRef({});
  const highlightTimeoutRef = useRef(null);
  const scrollTimerRef = useRef(null);
  const containerRef = useRef(null);

  // ── Reset on open ────────────────────────────────────────────────
  useEffect(() => {
    if (!show) {
      clearTimeout(highlightTimeoutRef.current);
      clearTimeout(scrollTimerRef.current);
      return;
    }
    setNumPages(null);
    setPdfDoc(null);
    setHighlightMatches(null);
    setCurrentPage(initialPage || 1);
    setPageInput(String(initialPage || 1));
    setRotation(0);
    // On small screens start fitted to the viewport width instead of 120%
    setScale(window.innerWidth <= 768
      ? Math.max(0.5, Math.min(1.2, (window.innerWidth - 40) / 595))
      : 1.2);
    setRenderedPages(new Set());
    pageRefs.current = {};
    setSearchQuery('');
    setShowSearchBar(false);
  }, [show, initialPage]);

  // ── Highlight targets: reranked chunks from the backend, or the legacy
  //    searchText prop treated as a single chunk ─────────────────────
  const effectiveHighlights = useMemo(() => {
    const valid = (Array.isArray(highlights) ? highlights : [])
      .filter(h => h && typeof h.text === 'string' && h.text.trim().length >= 15);
    if (valid.length > 0) return valid;
    if (searchText) {
      const clean = searchText.replace(/\[Page \d+\]/g, ' ').replace(/\s+/g, ' ').trim();
      if (clean.length >= 15) return [{ text: clean, page: initialPage || null }];
    }
    return [];
  }, [highlights, searchText, initialPage]);

  // ── Document loaded ──────────────────────────────────────────────
  const onDocumentLoadSuccess = useCallback((pdf) => {
    setPdfDoc(pdf);
    setNumPages(pdf.numPages);
    setRenderedPages(new Set(Array.from({ length: Math.min(5, pdf.numPages) }, (_, i) => i + 1)));
  }, []);

  // ── Jump to initial page after document loads ────────────────────
  useEffect(() => {
    if (!numPages || !initialPage || initialPage <= 1) return;
    const target = Math.min(initialPage, numPages);
    const timer = setTimeout(() => {
      setRenderedPages(prev => {
        const next = new Set(prev);
        for (let i = Math.max(1, target - 1); i <= Math.min(numPages, target + 3); i++) next.add(i);
        return next;
      });
      setTimeout(() => {
        const el = pageRefs.current[target];
        if (el && scrollContainerRef.current) {
          scrollContainerRef.current.scrollTo({ top: el.offsetTop - 16, behavior: 'smooth' });
        }
      }, 80);
    }, 300);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [numPages]);

  // ── Jump to page ─────────────────────────────────────────────────
  const jumpToPage = useCallback((page) => {
    if (!numPages) return;
    const target = Math.max(1, Math.min(numPages, page));
    setCurrentPage(target);
    setPageInput(String(target));
    setRenderedPages(prev => {
      const next = new Set(prev);
      for (let i = Math.max(1, target - 1); i <= Math.min(numPages, target + 3); i++) next.add(i);
      return next;
    });
    setTimeout(() => {
      const el = pageRefs.current[target];
      if (el && scrollContainerRef.current) {
        scrollContainerRef.current.scrollTo({ top: el.offsetTop - 16, behavior: 'smooth' });
      }
    }, 50);
  }, [numPages]);

  // ── Scroll tracking ──────────────────────────────────────────────
  const handleScroll = useCallback(() => {
    clearTimeout(scrollTimerRef.current);
    scrollTimerRef.current = setTimeout(() => {
      const container = scrollContainerRef.current;
      if (!container) return;
      const mid = container.scrollTop + container.clientHeight / 2;
      let best = 1, bestDist = Infinity;
      Object.entries(pageRefs.current).forEach(([p, el]) => {
        if (!el) return;
        const dist = Math.abs((el.offsetTop + el.offsetHeight / 2) - mid);
        if (dist < bestDist) { bestDist = dist; best = parseInt(p, 10); }
      });
      setCurrentPage(best);
      setPageInput(String(best));
    }, 80);
  }, []);

  // ── Placeholder visible — expand rendered set ────────────────────
  const handlePageVisible = useCallback((pageNum) => {
    setRenderedPages(prev => {
      if (prev.has(pageNum)) return prev;
      const next = new Set(prev);
      for (let i = Math.max(1, pageNum - 1); i <= Math.min(numPages || 9999, pageNum + 3); i++) next.add(i);
      return next;
    });
  }, [numPages]);

  // ── Locate the reranked chunks in the document once it loads ──────
  useEffect(() => {
    if (!show || !pdfDoc || effectiveHighlights.length === 0) return;
    let cancelled = false;
    (async () => {
      try {
        const result = await computeHighlightMatches(pdfDoc, effectiveHighlights);
        if (cancelled) return;
        setHighlightMatches(result);
        if (result.firstPage) jumpToPage(result.firstPage);
      } catch {
        // Matching is best-effort; the document still opens without marks
      }
    })();
    return () => { cancelled = true; };
  }, [show, pdfDoc, effectiveHighlights, jumpToPage]);

  // ── customTextRenderer: wrap matched ranges in <mark> ─────────────
  // Chunk highlights use the precomputed per-item ranges from the matching
  // engine; the manual find-in-document bar falls back to a simple
  // per-item substring match (fine for short user-typed queries).
  const customTextRenderer = useCallback((textItem) => {
    const { str, itemIndex, pageNumber: pageNum } = textItem;

    const ranges = highlightMatches?.pages?.[pageNum]?.[itemIndex];
    if (ranges && ranges.length) {
      let out = '';
      let pos = 0;
      for (const [s, e] of ranges) {
        out += escapeHtml(str.slice(pos, s)) +
          `<mark class="pdf-text-highlight">${escapeHtml(str.slice(s, e))}</mark>`;
        pos = e;
      }
      return out + escapeHtml(str.slice(pos));
    }

    const query = searchQuery.trim().toLowerCase();
    if (query.length >= 2) {
      const idx = str.toLowerCase().indexOf(query);
      if (idx !== -1) {
        return (
          escapeHtml(str.slice(0, idx)) +
          `<mark class="pdf-text-highlight">${escapeHtml(str.slice(idx, idx + query.length))}</mark>` +
          escapeHtml(str.slice(idx + query.length))
        );
      }
    }
    return escapeHtml(str);
  }, [highlightMatches, searchQuery]);

  // ── Scroll to first highlight after text layers render ────────────
  useEffect(() => {
    if (!show || !numPages) return;
    if (!highlightMatches?.firstPage && !searchQuery.trim()) return;
    clearTimeout(highlightTimeoutRef.current);
    highlightTimeoutRef.current = setTimeout(() => {
      const mark = containerRef.current?.querySelector('.react-pdf__Page .pdf-text-highlight')
        ?? document.querySelector('.react-pdf__Page .pdf-text-highlight');
      if (mark) mark.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 700);
    return () => clearTimeout(highlightTimeoutRef.current);
  }, [highlightMatches, searchQuery, renderedPages, scale, rotation, show, numPages]);

  // ── Zoom ─────────────────────────────────────────────────────────
  const zoomIn = useCallback(() => {
    setScale(prev => (ZOOM_STEPS.find(z => z > Math.round(prev * 100)) ?? 300) / 100);
  }, []);
  const zoomOut = useCallback(() => {
    setScale(prev => ([...ZOOM_STEPS].reverse().find(z => z < Math.round(prev * 100)) ?? 50) / 100);
  }, []);
  const fitToWidth = useCallback(() => {
    if (scrollContainerRef.current) {
      setScale(Math.max(0.5, Math.min(3, (scrollContainerRef.current.clientWidth - 48) / 595)));
    }
  }, []);

  // ── Rotate ────────────────────────────────────────────────────────
  const handleRotate = useCallback(() => setRotation(r => (r + 90) % 360), []);

  // ── Print ─────────────────────────────────────────────────────────
  const handlePrint = useCallback(() => {
    setIsPrinting(true);
    const iframe = document.createElement('iframe');
    iframe.style.cssText = 'position:fixed;top:-9999px;left:-9999px;width:1px;height:1px;border:none;';
    iframe.src = pdfUrl;
    document.body.appendChild(iframe);
    const cleanup = () => setTimeout(() => {
      try { document.body.removeChild(iframe); } catch {}
      setIsPrinting(false);
    }, 3000);
    iframe.onload = () => {
      try { iframe.contentWindow.focus(); iframe.contentWindow.print(); }
      catch { window.open(pdfUrl, '_blank'); }
      cleanup();
    };
    iframe.onerror = () => {
      try { document.body.removeChild(iframe); } catch {}
      window.open(pdfUrl, '_blank');
      setIsPrinting(false);
    };
  }, [pdfUrl]);

  // ── Download ──────────────────────────────────────────────────────
  const handleDownload = useCallback(async () => {
    setIsDownloading(true);
    try {
      const res = await fetch(pdfUrl, { credentials: 'include' });
      if (!res.ok) throw new Error('fetch failed');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = filename;
      document.body.appendChild(a); a.click();
      document.body.removeChild(a); URL.revokeObjectURL(url);
    } catch {
      const a = document.createElement('a');
      a.href = pdfUrl; a.download = filename; a.target = '_blank';
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
    } finally { setIsDownloading(false); }
  }, [pdfUrl, filename]);

  // ── Page input ────────────────────────────────────────────────────
  const handlePageInputChange = useCallback(e => setPageInput(e.target.value), []);
  const handlePageInputCommit = useCallback(e => {
    if (e.key === 'Enter' || e.type === 'blur') {
      const p = parseInt(pageInput, 10);
      if (!isNaN(p)) jumpToPage(p);
      else setPageInput(String(currentPage));
    }
  }, [pageInput, currentPage, jumpToPage]);

  // ── Memoized ──────────────────────────────────────────────────────
  const pdfOptions = useMemo(() => ({
    cMapUrl: `https://unpkg.com/pdfjs-dist@${pdfjs.version}/cmaps/`,
    cMapPacked: true,
    standardFontDataUrl: `https://unpkg.com/pdfjs-dist@${pdfjs.version}/standard_fonts/`,
  }), []);
  const fileConfig = useMemo(() => ({ url: pdfUrl }), [pdfUrl]);
  const pageNumbers = useMemo(() => Array.from({ length: numPages || 0 }, (_, i) => i + 1), [numPages]);

  const curPct = Math.round(scale * 100);
  const nearestZoom = ZOOM_STEPS.reduce((a, b) => Math.abs(b - curPct) < Math.abs(a - curPct) ? b : a);
  const landscape = rotation === 90 || rotation === 270;
  const pageW = Math.round((landscape ? 842 : 595) * scale);
  const pageH = Math.round((landscape ? 595 : 842) * scale);

  return (
    <Modal
      show={show}
      onHide={onHide}
      size="xl"
      centered
      className="pdf-viewer-modal"
      dialogClassName="pdf-viewer-dialog"
    >
      <Modal.Header closeButton className="bg-ut-orange text-white py-2 pe-3">
        <Modal.Title
          className="d-flex align-items-center gap-2 overflow-hidden me-2"
          style={{ flex: 1, minWidth: 0, fontSize: '0.95rem' }}
        >
          <i className="fas fa-file-pdf flex-shrink-0" />
          <span className="text-truncate">{filename}</span>
        </Modal.Title>
      </Modal.Header>

      <Modal.Body className="p-0 d-flex flex-column pdf-viewer-body">

        {/* ── TOOLBAR ──────────────────────────────────────────── */}
        <div className="pdf-toolbar">
          {/* Left group: thumbnails + prev/next/page-input */}
          <div className="pdf-toolbar-group">
            <button
              className={`pdf-tool-btn${showThumbnails ? ' active' : ''}`}
              onClick={() => setShowThumbnails(t => !t)}
              title="Toggle page thumbnails"
            >
              <i className="fas fa-th-large" />
            </button>
            <div className="pdf-toolbar-sep" />
            <button
              className="pdf-tool-btn"
              onClick={() => jumpToPage(currentPage - 1)}
              disabled={!numPages || currentPage <= 1}
              title="Previous page"
            >
              <i className="fas fa-chevron-up" />
            </button>
            <div className="pdf-page-input-group">
              <input
                type="text"
                className="pdf-page-input"
                value={pageInput}
                onChange={handlePageInputChange}
                onKeyDown={handlePageInputCommit}
                onBlur={handlePageInputCommit}
                disabled={!numPages}
                aria-label="Page number"
              />
              <span className="pdf-page-total">/ {numPages || '—'}</span>
            </div>
            <button
              className="pdf-tool-btn"
              onClick={() => jumpToPage(currentPage + 1)}
              disabled={!numPages || currentPage >= numPages}
              title="Next page"
            >
              <i className="fas fa-chevron-down" />
            </button>
          </div>

          <div className="pdf-toolbar-sep" />

          {/* Center group: zoom */}
          <div className="pdf-toolbar-group">
            <button
              className="pdf-tool-btn"
              onClick={zoomOut}
              disabled={scale <= 0.5}
              title="Zoom out"
            >
              <i className="fas fa-search-minus" />
            </button>
            <select
              className="pdf-zoom-select"
              value={nearestZoom}
              onChange={e => setScale(parseInt(e.target.value, 10) / 100)}
              title="Zoom level"
            >
              {ZOOM_STEPS.map(z => <option key={z} value={z}>{z}%</option>)}
            </select>
            <button
              className="pdf-tool-btn"
              onClick={zoomIn}
              disabled={scale >= 3}
              title="Zoom in"
            >
              <i className="fas fa-search-plus" />
            </button>
            <button
              className="pdf-tool-btn"
              onClick={fitToWidth}
              title="Fit to width"
            >
              <i className="fas fa-arrows-alt-h" />
            </button>
          </div>

          {/* Spacer pushes right group to the end */}
          <div style={{ flex: 1 }} />
          <div className="pdf-toolbar-sep" />

          {/* Right group: search, rotate, print, download, open */}
          <div className="pdf-toolbar-group">
            <button
              className={`pdf-tool-btn${showSearchBar ? ' active' : ''}`}
              onClick={() => setShowSearchBar(s => !s)}
              title="Find in document"
            >
              <i className="fas fa-search" />
            </button>
            <button
              className="pdf-tool-btn"
              onClick={handleRotate}
              title="Rotate clockwise"
            >
              <i className="fas fa-redo" />
            </button>
            <div className="pdf-toolbar-sep" />
            <button
              className="pdf-tool-btn"
              onClick={handlePrint}
              disabled={isPrinting || !numPages}
              title="Print"
            >
              {isPrinting
                ? <span className="spinner-border spinner-border-sm" style={{ width: 14, height: 14, borderWidth: 2 }} />
                : <i className="fas fa-print" />}
            </button>
            <button
              className="pdf-tool-btn"
              onClick={handleDownload}
              disabled={isDownloading || !pdfUrl}
              title="Download"
            >
              {isDownloading
                ? <span className="spinner-border spinner-border-sm" style={{ width: 14, height: 14, borderWidth: 2 }} />
                : <i className="fas fa-download" />}
            </button>
            <a
              href={pdfUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="pdf-tool-btn"
              title="Open in new tab"
              style={{ textDecoration: 'none' }}
            >
              <i className="fas fa-external-link-alt" />
            </a>
          </div>
        </div>

        {/* ── SEARCH BAR ───────────────────────────────────────── */}
        {showSearchBar && (
          <div className="pdf-search-bar">
            <i className="fas fa-search text-muted me-2" style={{ fontSize: '0.8rem' }} />
            <input
              type="text"
              className="pdf-search-input"
              placeholder="Find in document…"
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              autoFocus
            />
            <button
              className="pdf-tool-btn ms-1"
              onClick={() => { setShowSearchBar(false); setSearchQuery(''); }}
              title="Close search"
            >
              <i className="fas fa-times" />
            </button>
          </div>
        )}

        {/* ── CONTENT AREA ─────────────────────────────────────── */}
        <div className="pdf-content-area" ref={containerRef}>
          {pdfUrl ? (
            <Document
              file={fileConfig}
              options={pdfOptions}
              onLoadSuccess={onDocumentLoadSuccess}
              loading={
                <div className="pdf-state-overlay">
                  <Spinner animation="border" variant="light" />
                  <p className="mt-3 mb-0" style={{ color: 'rgba(255,255,255,0.6)' }}>Loading PDF…</p>
                </div>
              }
              error={
                <div className="pdf-state-overlay text-center">
                  <i className="fas fa-exclamation-triangle fa-3x text-warning mb-3" />
                  <p className="text-white mb-3">Failed to load PDF.</p>
                  <a href={pdfUrl} target="_blank" rel="noopener noreferrer" className="btn btn-outline-warning btn-sm">
                    <i className="fas fa-external-link-alt me-2" />
                    Open directly
                  </a>
                </div>
              }
            >
              <div style={{ display: 'flex', height: '100%', overflow: 'hidden' }}>
                {/* Thumbnail sidebar */}
                {showThumbnails && numPages && (
                  <div className="pdf-thumbnails-sidebar" ref={sidebarRef}>
                    {pageNumbers.map(p => (
                      <LazyThumbnail
                        key={p}
                        pageNum={p}
                        currentPage={currentPage}
                        onJump={jumpToPage}
                        sidebarRef={sidebarRef}
                      />
                    ))}
                  </div>
                )}

                {/* Main scroll area — all pages, lazy-rendered */}
                <div
                  className="pdf-scroll-area"
                  ref={scrollContainerRef}
                  onScroll={handleScroll}
                >
                  {pageNumbers.map(p => (
                    <div
                      key={p}
                      ref={el => { pageRefs.current[p] = el; }}
                      className="pdf-page-wrapper"
                    >
                      {renderedPages.has(p) ? (
                        <div className="pdf-page-shadow">
                          <Page
                            pageNumber={p}
                            scale={scale}
                            rotate={rotation}
                            renderTextLayer
                            renderAnnotationLayer
                            customTextRenderer={customTextRenderer}
                            loading={
                              <div style={{ width: pageW, height: pageH, background: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <span className="spinner-border spinner-border-sm text-secondary" />
                              </div>
                            }
                          />
                        </div>
                      ) : (
                        <PagePlaceholder
                          pageNum={p}
                          scale={scale}
                          rotation={rotation}
                          scrollRoot={scrollContainerRef}
                          onVisible={handlePageVisible}
                        />
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </Document>
          ) : (
            <div className="pdf-state-overlay">
              <Spinner animation="border" variant="light" />
            </div>
          )}
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default PDFViewerEnhanced;
